<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import communicationAPI from "@/services/standards/communication/communicationAPI";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

/**
 * 2.8.1 — Informe resumen de los auto-reportes de los trabajadores (condición insegura + salud).
 * Panel derivado (no captura): KPIs + distribución mensual + detalle, con descarga en PDF branded.
 */
export default {
  page: { title: "Informe resumen de auto-reportes" },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "2.8.1 Informe resumen de reportes de los trabajadores",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        {
          text: "2.8.1 Mecanismo de comunicación",
          href: `/forms/estandares/${this.$store.getters["company/getCompany"].id || null}/mecanismo-comunicacion`,
        },
        { text: "Informe resumen", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      years: [],
      year: new Date().getFullYear(),
      data: null,
      loading: true,
    };
  },
  computed: {
    chartSeries() {
      if (!this.data) return [];
      const insegura = [];
      const salud = [];
      for (let m = 1; m <= 12; m++) {
        const mm = this.data.monthly && this.data.monthly[m] ? this.data.monthly[m] : { insegura: 0, salud: 0 };
        insegura.push(mm.insegura || 0);
        salud.push(mm.salud || 0);
      }
      return [
        { name: "Condición insegura", data: insegura },
        { name: "Condición de salud", data: salud },
      ];
    },
    chartOptions() {
      return {
        chart: { type: "bar", height: 320, toolbar: { show: false }, stacked: false, fontFamily: "inherit" },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } },
        colors: ["#f7b84b", "#4fc6e1"],
        dataLabels: { enabled: false },
        legend: { position: "top" },
        grid: { borderColor: "#f1f1f1" },
        xaxis: { categories: MESES },
        yaxis: { title: { text: "N.° de reportes" }, labels: { formatter: (v) => Math.round(v) } },
        tooltip: { y: { formatter: (v) => `${v} reporte(s)` } },
      };
    },
  },
  methods: {
    rangeYear() {
      const now = new Date().getFullYear();
      for (let i = 2019; i <= now + 1; i++) this.years.push(i);
    },
    fetch() {
      this.loading = true;
      communicationAPI.summary(this.companyID, this.year)
        .then((r) => { this.data = r || null; this.loading = false; })
        .catch(() => { this.loading = false; });
    },
    descargarPdf() {
      window.open(communicationAPI.summaryPdfUrl(this.companyID, this.year));
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    this.rangeYear();
    this.fetch();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row animated animatedFadeInUp fadeInUp">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Barra de acciones -->
            <div class="row align-items-end mb-3">
              <div class="col-md-3">
                <label class="small mb-1">Año</label>
                <select class="form-control" v-model.number="year" @change="fetch">
                  <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                </select>
              </div>
              <div class="col-md-9 text-right">
                <button class="btn btn-secondary mr-1" @click="goBack"><i class="fas fa-arrow-left"></i></button>
                <button class="btn btn-primary" @click="descargarPdf" :disabled="loading">
                  <i class="mdi mdi-file-pdf-box"></i> Descargar informe (PDF)
                </button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5"><b-spinner variant="primary" /></div>

            <div v-else-if="data">
              <!-- KPIs -->
              <div class="row text-center mb-3">
                <div class="col-md-4">
                  <div class="card border">
                    <div class="card-body py-3">
                      <h2 class="mb-0 text-primary">{{ data.total }}</h2>
                      <span class="text-muted">Total de reportes</span>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card border">
                    <div class="card-body py-3">
                      <h2 class="mb-0" style="color:#f7b84b;">{{ data.total_insegura }}</h2>
                      <span class="text-muted">Condición insegura</span>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="card border">
                    <div class="card-body py-3">
                      <h2 class="mb-0" style="color:#4fc6e1;">{{ data.total_salud }}</h2>
                      <span class="text-muted">Condición de salud</span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="!data.total" class="alert alert-info">
                No se registraron auto-reportes de trabajadores en el año {{ year }}.
              </div>

              <template v-else>
                <!-- Distribución mensual -->
                <h5 class="text-primary">Distribución mensual</h5>
                <apexchart type="bar" height="320" :options="chartOptions" :series="chartSeries"></apexchart>

                <!-- Detalle -->
                <h5 class="text-primary mt-3">Detalle de reportes</h5>
                <div class="table-responsive">
                  <table class="table table-striped border-bottom">
                    <thead>
                      <tr>
                        <th class="text-center">N°</th>
                        <th class="text-center">Fecha</th>
                        <th>Colaborador</th>
                        <th>Tipo de reporte</th>
                        <th class="text-center">Soporte</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(r, i) in data.detalle" :key="i">
                        <td class="text-center">{{ i + 1 }}</td>
                        <td class="text-center">{{ r.fecha }}</td>
                        <td>{{ r.colaborador }}</td>
                        <td>{{ r.tipo }}</td>
                        <td class="text-center">
                          <span :class="r.tiene_archivo ? 'text-success' : 'text-muted'">
                            {{ r.tiene_archivo ? 'Sí' : 'No' }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
